import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import MuiAppBar from '@mui/material/AppBar';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import IconButton from '@mui/material/IconButton';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';

import { Branding } from '../AppProvider';
import { AccountProps } from '../Account';
import { BrandingContext } from '../shared/context';

import { ToolbarActions } from './ToolbarActions';
import { AppTitle, type AppTitleProps } from './AppTitle';

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  borderWidth: 0,
  boxShadow: 'none',
  borderBottomWidth: 1,
  borderStyle: 'solid',
  zIndex: theme.zIndex.drawer + 1,
  borderColor: (theme.vars ?? theme).palette.divider,
}));

export interface DashboardHeaderSlotProps {
  toolbarActions?: {};
  appTitle?: AppTitleProps;
  toolbarAccount?: AccountProps;
}

export interface DashboardHeaderSlots {
  /**
   * The component used for the app title section.
   * @default Link
   * @see [DashboardLayout#slots](https://mui.com/toolpad/core/react-dashboard-layout/#slots)
   */
  appTitle?: React.ElementType;
  /**
   * The toolbar actions component to be used.
   * @default ToolbarActions
   * @see [DashboardLayout#slots](https://mui.com/toolpad/core/react-dashboard-layout/#slots)
   */
  toolbarActions?: React.JSXElementConstructor<{}>;
  /**
   * The toolbar account component to be used.
   * @default Account
   * @deprecated Place your custom component on the right in the `toolbarActions` slot instead.
   * @see [DashboardLayout#slots](https://mui.com/toolpad/core/react-dashboard-layout/#slots)
   */
  toolbarAccount?: React.JSXElementConstructor<AccountProps>;
}

export interface DashboardHeaderProps {
  /**
   * If `true`, show menu button as if menu is expanded, otherwise show it as if menu is collapsed.
   */
  menuOpen: boolean;
  /**
   * Whether the menu icon should always be hidden.
   * @default false
   */
  hideMenuButton?: boolean;
  /**
   * Branding options for the header.
   * @default null
   */
  branding?: Branding | null;
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots?: DashboardHeaderSlots;
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps?: DashboardHeaderSlotProps;
  /**
   * Callback fired when the menu button is clicked.
   */
  onToggleMenu: (open: boolean) => void;
}
/**
 *
 * Demos:
 *
 * - [Dashboard Layout](https://mui.com/toolpad/core/react-dashboard-layout/)
 *
 * API:
 *
 * - [DashboardHeader API](https://mui.com/toolpad/core/api/dashboard-header)
 */
function DashboardHeader(props: DashboardHeaderProps) {
  const {
    slots,
    menuOpen,
    slotProps,
    onToggleMenu,
    hideMenuButton,
    branding: brandingProp,
  } = props;

  const brandingContext = React.useContext(BrandingContext);

  const branding = { ...brandingContext, ...brandingProp };

  const handleMenuOpen = React.useCallback(() => {
    onToggleMenu(!menuOpen);
  }, [menuOpen, onToggleMenu]);

  const getMenuIcon = React.useCallback(
    (isExpanded: boolean) => {
      const expandMenuActionText = 'Expand';
      const collapseMenuActionText = 'Collapse';

      return (
        <Tooltip
          enterDelay={1000}
          title={`${isExpanded ? collapseMenuActionText : expandMenuActionText} menu`}
        >
          <div>
            <IconButton
              onClick={handleMenuOpen}
              aria-label={`${isExpanded ? collapseMenuActionText : expandMenuActionText} navigation menu`}
            >
              {isExpanded ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </div>
        </Tooltip>
      );
    },
    [handleMenuOpen]
  );

  const ToolbarActionsSlot = slots?.toolbarActions ?? ToolbarActions;
  const ToolbarAccountSlot = slots?.toolbarAccount ?? (() => null);

  return (
    <AppBar color="inherit" position="absolute" sx={{ displayPrint: 'none' }}>
      <Toolbar sx={{ mx: { sm: -1, xs: -0.75 }, backgroundColor: 'inherit' }}>
        <Stack
          direction="row"
          sx={{
            width: '100%',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row">
            {!hideMenuButton ? (
              <React.Fragment>
                <Box
                  sx={{
                    mr: { sm: 1 },
                    display: { md: 'none', xs: 'block' },
                  }}
                >
                  {getMenuIcon(menuOpen)}
                </Box>
                <Box
                  sx={{
                    mr: 1,
                    display: { xs: 'none', md: 'block' },
                  }}
                >
                  {getMenuIcon(menuOpen)}
                </Box>
              </React.Fragment>
            ) : null}
            {slots?.appTitle ? (
              <slots.appTitle {...slotProps?.appTitle} />
            ) : (
              /* Hierarchy of application of `branding`
               * 1. Branding prop passed in the `slotProps.appTitle`
               * 2. Branding prop passed to the `DashboardLayout`
               * 3. Branding prop passed to the `AppProvider`
               */
              <AppTitle branding={branding} {...slotProps?.appTitle} />
            )}
          </Stack>
          <Stack
            spacing={1}
            direction="row"
            sx={{
              marginLeft: 'auto',
              alignItems: 'center',
            }}
          >
            <ToolbarActionsSlot {...slotProps?.toolbarActions} />
            <ToolbarAccountSlot {...slotProps?.toolbarAccount} />
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

DashboardHeader.propTypes /* remove-proptypes */ = {
  /**
   * Whether the menu icon should always be hidden.
   * @default false
   */
  hideMenuButton: PropTypes.bool,
  /**
   * If `true`, show menu button as if menu is expanded, otherwise show it as if menu is collapsed.
   */
  menuOpen: PropTypes.bool.isRequired,
  /**
   * Callback fired when the menu button is clicked.
   */
  onToggleMenu: PropTypes.func.isRequired,
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * Branding options for the header.
   * @default null
   */
  branding: PropTypes.shape({
    logo: PropTypes.node,
    title: PropTypes.string,
    homeUrl: PropTypes.string,
  }),
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots: PropTypes.shape({
    appTitle: PropTypes.elementType,
    toolbarAccount: PropTypes.elementType,
    toolbarActions: PropTypes.elementType,
  }),
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps: PropTypes.shape({
    toolbarActions: PropTypes.object,
    appTitle: PropTypes.shape({
      branding: PropTypes.shape({
        logo: PropTypes.node,
        title: PropTypes.string,
        homeUrl: PropTypes.string,
      }),
    }),
    toolbarAccount: PropTypes.shape({
      localeText: PropTypes.object,
      slotProps: PropTypes.shape({
        popover: PropTypes.object,
        preview: PropTypes.object,
        signInButton: PropTypes.object,
        signOutButton: PropTypes.object,
        popoverContent: PropTypes.object,
      }),
      slots: PropTypes.shape({
        popover: PropTypes.elementType,
        preview: PropTypes.elementType,
        signInButton: PropTypes.elementType,
        signOutButton: PropTypes.elementType,
        popoverContent: PropTypes.elementType,
      }),
    }),
  }),
} as any;

export { DashboardHeader };
